import { buildProjectUrl } from "./core/config.js";
import {
  getVerifyTriggerPosition,
  getVerifyTriggerType,
} from "./core/validate-survey.js";
import {
  createUserSoundPortal,
  type PortalContext,
  type UserSoundPortalInstance,
  type UserSoundPortalOptions,
} from "./browser/portal.js";
import { isIdentifiedPortalContext } from "./browser/widget-session.js";

type OpenDialogOptions = {
  projectId?: string;
  context?: PortalContext;
  baseUrl?: string;
  anchorElement?: HTMLElement;
  showBackdrop?: boolean;
};

type InitConfig = {
  baseUrl?: string;
  context?: PortalContext;
  /** When set with `baseUrl`, starts a `/widget/verify/` request so `create` can reuse it. */
  projectId?: string;
};

interface UserSoundGlobal {
  init(config: InitConfig): void;
  createPortal(options: UserSoundPortalOptions): UserSoundPortalInstance;
  create(options: TriggerOptions): void;
  embed(options: EmbedOptions): void;
}

declare global {
  interface Window {
    UserSound?: UserSoundGlobal;
  }
}

type EmbedOptions = {
  element: HTMLElement | string;
  projectId?: string;
  baseUrl?: string;
  context?: PortalContext;
  autoResize?: boolean;
};

type TriggerVariant = "floating" | "tab" | "container";
type TriggerPosition =
  | "bottom-right"
  | "bottom-left"
  | "top-right"
  | "top-left"
  | "left"
  | "right";

type TriggerOptions = OpenDialogOptions & {
  position?: TriggerPosition;
  variant?: TriggerVariant;
  label?: string;
  containerElement?: HTMLElement | string;
};

let defaultBaseUrl: string | undefined;
let defaultContext: PortalContext | undefined;
let defaultProjectId: string | undefined;

function effectiveProjectId(explicit?: string): string | undefined {
  const t = typeof explicit === "string" ? explicit.trim() : "";
  if (t) return t;
  return defaultProjectId;
}

type VerifyPrefetchState = {
  key: string;
  promise: Promise<Record<string, unknown> | null>;
};

let verifyPrefetch: VerifyPrefetchState | null = null;

function verifyPrefetchKey(
  projectId: string,
  baseUrl: string,
  context: PortalContext | undefined
): string {
  const uid = (context as { user_id?: string } | undefined)?.user_id ?? "";
  return `${baseUrl}\0${projectId}\0${uid}`;
}

async function fetchVerifyPayload(
  projectId: string,
  context: PortalContext | undefined,
  baseUrl: string
): Promise<Record<string, unknown> | null> {
  try {
    const portalPageUrl = buildProjectUrl(
      projectId,
      (context as { user_id?: string })?.user_id ?? "",
      baseUrl
    );
    const verifyUrl = buildVerifyUrl(portalPageUrl, projectId, context);
    const res = await fetch(verifyUrl.href, { method: "GET", mode: "cors", cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json().catch(() => null);
    if (!data || typeof data !== "object") return null;
    return data as Record<string, unknown>;
  } catch {
    return null;
  }
}

function scheduleVerifyPrefetch(
  projectId: string,
  context: PortalContext | undefined,
  baseUrl: string
): void {
  const key = verifyPrefetchKey(projectId, baseUrl, context);
  if (verifyPrefetch?.key === key) return;
  verifyPrefetch = {
    key,
    promise: fetchVerifyPayload(projectId, context, baseUrl),
  };
}

let backdropEl: HTMLDivElement | null = null;
let dialogContainerEl: HTMLDivElement | null = null;
let portalInstance: UserSoundPortalInstance | null = null;
let isOpen = false;
let previousActiveElement: HTMLElement | null = null;
let currentAnchorEl: HTMLElement | null = null;
let resizeListener: (() => void) | null = null;

interface PortalLoadErrorLike {
  url?: string;
  reason?: string;
  statusCode?: number;
  message?: string;
}

let currentError: PortalLoadErrorLike | null = null;
let errorOverlayEl: HTMLDivElement | null = null;
let loadingOverlayEl: HTMLDivElement | null = null;
let portalContentEl: HTMLDivElement | null = null;
let currentContext: PortalContext | undefined;

const ANCHOR_GAP = 8;
const SPINNER_STYLE_ID = "usersound-spinner-keyframes";

function whenBodyReady(fn: () => void): void {
  if (document.body) {
    fn();
  } else {
    document.addEventListener("DOMContentLoaded", fn, { once: true });
  }
}

function injectSpinnerKeyframes(): void {
  if (typeof document === "undefined" || document.getElementById(SPINNER_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = SPINNER_STYLE_ID;
  style.textContent = "@keyframes usersound-spin{to{transform:rotate(360deg)}}";
  document.head.appendChild(style);
}

function hideLoadingOverlay(): void {
  if (loadingOverlayEl) {
    loadingOverlayEl.style.display = "none";
  }
}
const VIEWPORT_INSET = 16;

function mergeVerifyResponseId(
  context: PortalContext | undefined,
  verifyBody: Record<string, unknown> | null | undefined
): PortalContext | undefined {
  if (!verifyBody || typeof verifyBody !== "object") return context;
  if (!isIdentifiedPortalContext(context ?? {})) return context;
  const rid = verifyBody.response_id;
  if (typeof rid !== "string" || !rid.trim()) return context;
  return { ...(context ?? {}), response_id: rid.trim() };
}

function buildVerifyUrl(
  portalPageUrl: string,
  projectId: string,
  context: PortalContext | undefined
): URL {
  const verifyUrl = new URL("/widget/verify/", portalPageUrl);
  verifyUrl.searchParams.set("project_uuid", projectId);
  verifyUrl.searchParams.set(
    "embed_host",
    typeof window !== "undefined" ? window.location.host : ""
  );
  verifyUrl.searchParams.set("sdk_platform", "web");
  verifyUrl.searchParams.set(
    "user_id",
    (context as { user_id?: string })?.user_id ?? ""
  );
  const ctx = (context ?? {}) as Record<string, string | undefined>;
  if (ctx.user_name) {
    verifyUrl.searchParams.set("user_name", ctx.user_name);
  }
  if (ctx.first_name) {
    verifyUrl.searchParams.set("first_name", ctx.first_name);
  }
  if (ctx.email) {
    verifyUrl.searchParams.set("email", ctx.email);
  }
  return verifyUrl;
}

function getAnchorPosition(
  anchorRect: DOMRect,
  dialogWidth: number,
  dialogHeight: number
): { top: number; left: number; maxHeight: number } {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  const maxLeft = vw - dialogWidth - VIEWPORT_INSET;
  const left = Math.max(
    VIEWPORT_INSET,
    Math.min(maxLeft, anchorRect.left + (anchorRect.width - dialogWidth) / 2)
  );
  const spaceBelow = vh - anchorRect.bottom - ANCHOR_GAP - VIEWPORT_INSET;
  const spaceAbove = anchorRect.top - ANCHOR_GAP - VIEWPORT_INSET;
  const showBelow = spaceBelow >= dialogHeight || spaceBelow >= spaceAbove;
  const maxHeight = showBelow ? spaceBelow : spaceAbove;
  let top = showBelow
    ? anchorRect.bottom + ANCHOR_GAP
    : anchorRect.top - ANCHOR_GAP - Math.min(dialogHeight, maxHeight);
  top = Math.max(
    VIEWPORT_INSET,
    Math.min(vh - dialogHeight - VIEWPORT_INSET, top)
  );
  return { top, left, maxHeight };
}

function renderErrorOverlay(): void {
  if (!dialogContainerEl) return;
  if (!currentError) {
    if (errorOverlayEl?.parentElement) {
      errorOverlayEl.parentElement.removeChild(errorOverlayEl);
    }
    errorOverlayEl = null;
    return;
  }
  if (!errorOverlayEl) {
    errorOverlayEl = document.createElement("div");
    errorOverlayEl.style.cssText =
      "position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:24px;background:#fff;color:#111827;z-index:5";
    dialogContainerEl.appendChild(errorOverlayEl);
  }
  errorOverlayEl.innerHTML = "";
  errorOverlayEl.style.textAlign = "center";

  const title = document.createElement("p");
  title.textContent = "We couldn't load the survey";
  title.style.cssText = "margin:0;font-size:16px;font-weight:600";
  errorOverlayEl.appendChild(title);

  const message = document.createElement("p");
  message.style.cssText = "margin:8px 0 0;font-size:14px;opacity:0.9";
  message.textContent =
    currentError.reason === "cors"
      ? "Your browser blocked the survey due to CORS configuration."
      : currentError.statusCode != null
        ? `The survey URL returned an error (${currentError.statusCode}).`
        : "The survey URL may be invalid or unavailable. Please check your configuration.";
  errorOverlayEl.appendChild(message);

  if (currentError.reason === "cors") {
    const corsTips = document.createElement("div");
    corsTips.style.cssText =
      "margin-top:12px;font-size:12px;text-align:left;width:100%;max-width:520px;padding:12px;border-radius:8px;background:#111827;color:#e5e7eb;font-family:SFMono-Regular,Menlo,Monaco,Consolas,monospace;overflow-x:auto";
    corsTips.innerHTML = "CORS tips for the survey URL:<pre style=\"margin:8px 0 0;white-space:pre-wrap\">// On your API / survey host, ensure:\nAccess-Control-Allow-Origin: https://your-app-domain.com\nAccess-Control-Allow-Methods: GET, HEAD, OPTIONS\nAccess-Control-Allow-Headers: Content-Type</pre>";
    errorOverlayEl.appendChild(corsTips);
  }

  const debugWrap = document.createElement("div");
  debugWrap.style.cssText =
    "margin-top:12px;font-size:11px;text-align:left;width:100%;max-width:520px;padding:10px;border-radius:6px;background:#f3f4f6;color:#374151;font-family:SFMono-Regular,Menlo,Monaco,Consolas,monospace;overflow-x:auto";
  debugWrap.innerHTML =
    "Debug report:<pre style=\"margin:4px 0 0;white-space:pre-wrap;word-break:break-all\">" +
    [
      `url: ${currentError.url ?? "(not available)"}`,
      `reason: ${currentError.reason ?? "unknown"}`,
      currentError.statusCode != null ? `statusCode: ${currentError.statusCode}` : "statusCode: (none)",
      `message: ${currentError.message ?? "(none)"}`,
    ].join("\n") +
    "</pre>";
  errorOverlayEl.appendChild(debugWrap);
  if (currentError.reason !== "invalid_url") {
    const retry = document.createElement("button");
    retry.type = "button";
    retry.textContent = "Try again";
    retry.style.cssText =
      "margin-top:16px;padding:10px 20px;border-radius:8px;border:none;background:#dc2626;color:#fff;font-size:14px;font-weight:600;cursor:pointer";
    retry.onclick = () => {
      currentError = null;
      renderErrorOverlay();
      portalInstance?.updateContext(currentContext ?? {});
    };
    errorOverlayEl.appendChild(retry);
  }
}

function realOpenDialog(options: OpenDialogOptions): void {
  if (typeof document === "undefined") return;
  const projectId = effectiveProjectId(options.projectId);
  if (!projectId) {
    console.error("UserSound: projectId is required to open the dialog");
    return;
  }
  const merged: OpenDialogOptions & { projectId: string } = { ...options, projectId };
  if (!("baseUrl" in merged) && defaultBaseUrl) (merged as OpenDialogOptions & { baseUrl: string }).baseUrl = defaultBaseUrl;
  if (!("context" in merged) && defaultContext) (merged as OpenDialogOptions & { context: PortalContext }).context = defaultContext;
  else if (merged.context && defaultContext) merged.context = { ...defaultContext, ...merged.context };
  currentContext = merged.context;
  const baseUrl = merged.baseUrl;
  const proceed = () => doOpenDialog(merged);
  if (baseUrl) {
    try {
      const portalPageUrl = buildProjectUrl(
        merged.projectId,
        (merged.context as { user_id?: string })?.user_id ?? "",
        baseUrl
      );
      const verifyUrl = buildVerifyUrl(portalPageUrl, merged.projectId, merged.context);
      fetch(verifyUrl.href, { method: "GET", mode: "cors", cache: "no-store" })
        .then((r) => r.json().catch(() => null))
        .then((d: { allowed?: boolean } | null) => {
          if (d && d.allowed === false) return;
          // Identified users: verify may return response_id for /survey/.../response_id/ resume (legacy parity). Anonymous: unchanged—URL uses localStorage only.
          if (d && typeof d === "object") {
            const next = mergeVerifyResponseId(
              merged.context,
              d as Record<string, unknown>
            );
            if (next) merged.context = next;
          }
          currentContext = merged.context;
          proceed();
        })
        .catch(proceed);
    } catch {
      proceed();
    }
  } else proceed();
}

function doOpenDialog(merged: OpenDialogOptions & { projectId: string }): void {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  const MOBILE_BREAKPOINT = 768;
  const useAnchor =
    merged.anchorElement && vw >= MOBILE_BREAKPOINT && document.contains(merged.anchorElement);
  const showBackdrop = merged.showBackdrop ?? (useAnchor ? false : true);
  currentAnchorEl = useAnchor ? merged.anchorElement! : null;

  if (!backdropEl) {
    backdropEl = document.createElement("div");
    backdropEl.style.cssText = "position:fixed;inset:0;display:flex;z-index:9999";
    backdropEl.onclick = (e) => { if (e.target === backdropEl) closeDialog(); };
  }
  backdropEl.style.backgroundColor = showBackdrop ? "rgba(15, 23, 42, 0.55)" : "transparent";

  if (!dialogContainerEl) {
    dialogContainerEl = document.createElement("div");
    dialogContainerEl.style.backgroundColor = "#ffffff";
  }

  const dialogWidth = 520;
  const dialogHeight = Math.min(0.9 * vh, vh - VIEWPORT_INSET * 2);

  if (vw < MOBILE_BREAKPOINT) {
    backdropEl.style.alignItems = "stretch";
    backdropEl.style.justifyContent = "stretch";
    backdropEl.style.padding = "0";
    dialogContainerEl.style.cssText = "position:relative;width:100%;height:100%;max-width:none;max-height:none;border-radius:0;box-shadow:none;border:none;display:flex;flex-direction:column;overflow:hidden";
  } else if (useAnchor && merged.anchorElement) {
    const rect = merged.anchorElement.getBoundingClientRect();
    const { top, left, maxHeight } = getAnchorPosition(rect, dialogWidth, dialogHeight);
    backdropEl.style.alignItems = "flex-start";
    backdropEl.style.justifyContent = "flex-start";
    backdropEl.style.padding = "0";
    dialogContainerEl.style.cssText =
      `position:fixed;top:${top}px;left:${left}px;width:${dialogWidth}px;height:${dialogHeight}px;max-width:min(100%,calc(100vw - ${VIEWPORT_INSET * 2}px));max-height:${maxHeight}px;border-radius:16px;box-shadow:0 18px 45px rgba(15,23,42,0.35);border:1px solid rgba(15,23,42,0.06);display:flex;flex-direction:column;overflow:hidden`;
    const updatePos = () => {
      if (!dialogContainerEl || !currentAnchorEl || !document.contains(currentAnchorEl)) return;
      const r = currentAnchorEl.getBoundingClientRect();
      const p = getAnchorPosition(r, dialogWidth, dialogHeight);
      dialogContainerEl!.style.top = `${p.top}px`;
      dialogContainerEl!.style.left = `${p.left}px`;
      dialogContainerEl!.style.maxHeight = `${p.maxHeight}px`;
    };
    if (resizeListener) window.removeEventListener("resize", resizeListener);
    resizeListener = updatePos;
    window.addEventListener("resize", resizeListener);
  } else {
    if (resizeListener) {
      window.removeEventListener("resize", resizeListener);
      resizeListener = null;
    }
    backdropEl.style.alignItems = "center";
    backdropEl.style.justifyContent = "center";
    backdropEl.style.padding = "16px";
    dialogContainerEl.style.cssText =
      "position:relative;width:100%;max-width:520px;height:80vh;max-height:96vh;border-radius:16px;box-shadow:0 18px 45px rgba(15,23,42,0.35);border:1px solid rgba(15,23,42,0.06);display:flex;flex-direction:column;overflow:hidden";
  }

  dialogContainerEl.setAttribute("role", "dialog");
  dialogContainerEl.setAttribute("aria-modal", "true");
  if (!dialogContainerEl.getAttribute("aria-label")) dialogContainerEl.setAttribute("aria-label", "UserSound survey");
  dialogContainerEl.tabIndex = -1;

  if (!backdropEl.parentElement) whenBodyReady(() => { if (!backdropEl!.parentElement) document.body.appendChild(backdropEl!); });
  if (!dialogContainerEl.parentElement) backdropEl.appendChild(dialogContainerEl);

  if (!dialogContainerEl.querySelector("[data-usersound-close]")) {
    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.setAttribute("data-usersound-close", "true");
    closeBtn.setAttribute("aria-label", "Close");
    closeBtn.style.cssText =
      "position:absolute;top:10px;right:10px;width:44px;height:44px;min-width:44px;min-height:44px;padding:0;border-radius:22px;border:none;background:#ffffff;color:#111827;cursor:pointer;display:flex;align-items:center;justify-content:center;z-index:20;box-shadow:0 2px 10px rgba(15,23,42,0.14);flex-shrink:0";
    closeBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
    closeBtn.onclick = closeDialog;
    dialogContainerEl.appendChild(closeBtn);
  }

  if (!portalContentEl) {
    portalContentEl = document.createElement("div");
    portalContentEl.style.cssText =
      "flex:1;position:relative;overflow:hidden;min-height:0;display:flex;flex-direction:column;background:#ffffff";

    loadingOverlayEl = document.createElement("div");
    loadingOverlayEl.setAttribute("aria-hidden", "true");
    loadingOverlayEl.style.cssText =
      "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:#ffffff;z-index:5";
    injectSpinnerKeyframes();
    const spinner = document.createElement("div");
    spinner.style.cssText =
      "width:28px;height:28px;border:3px solid #e5e7eb;border-top-color:#6366f1;border-radius:50%;animation:usersound-spin 0.7s linear infinite";
    loadingOverlayEl.appendChild(spinner);
    portalContentEl.appendChild(loadingOverlayEl);

    dialogContainerEl.appendChild(portalContentEl);
  } else if (!portalInstance) {
    loadingOverlayEl!.style.display = "flex";
  }

  if (!portalInstance) {
    portalInstance = createUserSoundPortal({
      projectId: merged.projectId,
      context: merged.context,
      baseUrl: merged.baseUrl,
      onPortalLoadError(e) {
        hideLoadingOverlay();
        currentError = { url: e.url, reason: e.reason, statusCode: e.statusCode, message: e.message };
        renderErrorOverlay();
      },
    });
    portalInstance.mount(portalContentEl!);
    const iframe = portalContentEl!.querySelector("iframe");
    if (iframe) {
      iframe.addEventListener("load", hideLoadingOverlay, { once: true });
    } else {
      hideLoadingOverlay();
    }
  } else {
    portalInstance.updateContext(merged.context ?? {});
    hideLoadingOverlay();
  }

  backdropEl.style.display = "flex";
  previousActiveElement = document.activeElement as HTMLElement | null;
  isOpen = true;
  currentError = null;
  renderErrorOverlay();
  dialogContainerEl.focus();
  portalInstance.open();
}

function closeDialog(): void {
  portalInstance?.close();
  if (backdropEl) backdropEl.style.display = "none";
  if (resizeListener) {
    window.removeEventListener("resize", resizeListener);
    resizeListener = null;
  }
  currentAnchorEl = null;
  isOpen = false;
  if (previousActiveElement && document.contains(previousActiveElement)) {
    try { previousActiveElement.focus(); } catch {}
  }
  previousActiveElement = null;
}

function realCreatePortal(options: UserSoundPortalOptions): UserSoundPortalInstance {
  const projectId = effectiveProjectId(options.projectId);
  if (!projectId) {
    throw new Error("UserSound.createPortal: projectId is required");
  }
  return createUserSoundPortal({
    ...options,
    projectId,
    baseUrl: options.baseUrl ?? defaultBaseUrl,
    context:
      options.context && defaultContext
        ? { ...defaultContext, ...options.context }
        : options.context ?? defaultContext,
  });
}

function create(options: TriggerOptions): void {
  if (typeof document === "undefined") return;
  let taglineEl: HTMLDivElement | null = null;
  const { position, variant, label, containerElement, ...dialogRest } = options;
  const projectId = effectiveProjectId(dialogRest.projectId);
  if (!projectId) {
    console.error("UserSound.create: projectId is required");
    return;
  }
  const dialog = { ...dialogRest, projectId };
  const openFrom = (anchor?: HTMLElement) => {
    if (taglineEl) taglineEl.style.display = "none";
    realOpenDialog({ ...dialog, anchorElement: anchor, showBackdrop: dialog.showBackdrop ?? false });
  };

  if (variant === "container") {
    const bindContainer = (el: HTMLElement) => {
      el.setAttribute("role", "button");
      el.setAttribute("tabindex", "0");
      el.setAttribute("aria-label", "Open UserSound survey");
      el.onclick = () => openFrom(el);
      el.onkeydown = (e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openFrom(el); }
      };
    };

    if (containerElement instanceof HTMLElement) {
      bindContainer(containerElement);
      return;
    }

    if (typeof containerElement === "string") {
      const selector = containerElement;
      whenBodyReady(() => {
        const el = document.querySelector(selector);
        if (el instanceof HTMLElement) {
          bindContainer(el);
        } else {
          console.error(
            `UserSound.create: no element matched selector ${JSON.stringify(selector)}`
          );
        }
      });
      return;
    }

    console.error("UserSound.create: variant 'container' requires containerElement");
    return;
  }

  /** When both are omitted, variant and placement come only from `/widget/verify/`. */
  const remoteLayout = variant === undefined && position === undefined;
  const baseUrl = dialog.baseUrl ?? defaultBaseUrl;
  if (remoteLayout && !baseUrl) {
    console.error(
      "UserSound.create: baseUrl is required when variant and position are omitted (set UserSound.init({ baseUrl }))."
    );
    return;
  }

  const baseBackground =
    "linear-gradient(135deg, #2563EB 0%, #4F46E5 50%, #EC4899 100%)";
  const defaultTabLabel = label ?? "Feedback";
  let resolvedVariant: TriggerVariant = variant === "tab" ? "tab" : "floating";
  let resolvedPosition: TriggerPosition = position ?? "bottom-right";

  const btn = document.createElement("button");
  btn.type = "button";
  btn.setAttribute("aria-label", "Open UserSound survey");
  btn.style.position = "fixed";
  btn.style.zIndex = "9998";
  btn.style.opacity = "0";
  btn.style.transition = "opacity 0.2s ease";
  btn.style.border = "none";
  btn.style.color = "#ffffff";
  btn.style.display = "flex";
  btn.style.alignItems = "center";
  btn.style.justifyContent = "center";
  btn.style.boxShadow = "0 12px 30px rgba(15, 23, 42, 0.45)";
  btn.style.cursor = "pointer";

  const showTrigger = (background: string, tabLabel: string) => {
    btn.style.background = background;
    if (resolvedVariant === "tab") {
      btn.textContent = tabLabel;
    }
    btn.style.opacity = "1";
  };

  const clearPlacementStyles = () => {
    btn.style.top = "";
    btn.style.right = "";
    btn.style.bottom = "";
    btn.style.left = "";
    btn.style.transform = "";
  };

  const applyTriggerLayout = (
    nextVariant: TriggerVariant,
    nextPosition: TriggerPosition,
    tabText: string
  ) => {
    resolvedVariant = nextVariant === "tab" ? "tab" : "floating";
    resolvedPosition = nextPosition;

    btn.textContent = "";
    btn.innerHTML = "";
    clearPlacementStyles();

    btn.style.width = "";
    btn.style.height = "";
    btn.style.minWidth = "";
    btn.style.paddingInline = "";
    btn.style.paddingBlock = "";
    btn.style.borderRadius = "";
    btn.style.whiteSpace = "";
    btn.style.fontWeight = "";
    btn.style.fontSize = "";
    btn.style.overflow = "";
    btn.style.padding = "";

    if (resolvedVariant === "tab") {
      const side =
        resolvedPosition === "left" ||
        resolvedPosition === "bottom-left" ||
        resolvedPosition === "top-left"
          ? "left"
          : "right";
      btn.style.top = "50%";
      btn.style.transform = "translateY(-50%)";
      if (side === "left") btn.style.left = "0";
      else btn.style.right = "0";

      btn.textContent = tabText;
      btn.style.minWidth = "56px";
      btn.style.paddingInline = "16px";
      btn.style.paddingBlock = "12px";
      btn.style.borderRadius = "4px";
      btn.style.whiteSpace = "nowrap";
      btn.style.fontWeight = "600";
      btn.style.fontSize = "14px";
    } else {
      switch (resolvedPosition) {
        case "bottom-left":
          btn.style.bottom = "24px";
          btn.style.left = "24px";
          break;
        case "top-right":
          btn.style.top = "24px";
          btn.style.right = "24px";
          break;
        case "top-left":
          btn.style.top = "24px";
          btn.style.left = "24px";
          break;
        case "left":
          btn.style.top = "50%";
          btn.style.left = "0";
          btn.style.transform = "translateY(-50%)";
          break;
        case "right":
          btn.style.top = "50%";
          btn.style.right = "0";
          btn.style.transform = "translateY(-50%)";
          break;
        case "bottom-right":
        default:
          btn.style.bottom = "24px";
          btn.style.right = "24px";
          break;
      }
      btn.style.width = "56px";
      btn.style.height = "56px";
      btn.style.borderRadius = "9999px";
      btn.innerHTML =
        '<svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24"><path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 1 0 6 0V5a3 3 0 0 0-3-3zm-5 8a1 1 0 0 0-2 0 7 7 0 0 0 6 6.93V20H9a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2h-2v-3.07A7 7 0 0 0 19 10a1 1 0 1 0-2 0 5 5 0 1 1-10 0z"/></svg>';
    }
  };

  if (!remoteLayout) {
    applyTriggerLayout(resolvedVariant, resolvedPosition, defaultTabLabel);
    btn.style.background = baseBackground;
  } else {
    btn.style.width = "0";
    btn.style.height = "0";
    btn.style.overflow = "hidden";
    btn.style.padding = "0";
  }

  btn.onclick = () => openFrom(btn);
  btn.onkeydown = (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openFrom(btn); } };

  // Tagline bubble element
  const createTaglineBubble = (text: string) => {
    taglineEl = document.createElement("div");
    taglineEl.style.cssText =
      "position:fixed;z-index:9997;max-width:240px;padding:8px 12px;border-radius:8px;background:#ffffff;color:#111827;font-size:13px;line-height:1.4;box-shadow:0 4px 16px rgba(15,23,42,0.18);display:flex;align-items:center;gap:6px;opacity:0;transition:opacity 0.2s ease;font-family:system-ui,-apple-system,sans-serif";

    const textSpan = document.createElement("span");
    textSpan.style.flex = "1";
    textSpan.textContent = text;
    taglineEl.appendChild(textSpan);

    const dismissBtn = document.createElement("button");
    dismissBtn.type = "button";
    dismissBtn.setAttribute("aria-label", "Dismiss");
    dismissBtn.style.cssText =
      "background:none;border:none;cursor:pointer;padding:0;color:#9CA3AF;font-size:16px;line-height:1;flex-shrink:0";
    dismissBtn.textContent = "\u00D7";
    dismissBtn.onclick = (e) => {
      e.stopPropagation();
      taglineEl?.remove();
      taglineEl = null;
    };
    taglineEl.appendChild(dismissBtn);

    whenBodyReady(() => {
      if (taglineEl) document.body.appendChild(taglineEl);
    });
  };

  const positionTaglineBubble = () => {
    if (!taglineEl) return;
    taglineEl.style.top = "";
    taglineEl.style.right = "";
    taglineEl.style.bottom = "";
    taglineEl.style.left = "";
    taglineEl.style.transform = "";

    if (resolvedVariant === "tab") {
      const side =
        resolvedPosition === "left" ||
        resolvedPosition === "bottom-left" ||
        resolvedPosition === "top-left"
          ? "left"
          : "right";
      taglineEl.style.top = "50%";
      taglineEl.style.transform = "translateY(-50%)";
      if (side === "left") taglineEl.style.left = "60px";
      else taglineEl.style.right = "60px";
    } else {
      switch (resolvedPosition) {
        case "bottom-left":
          taglineEl.style.bottom = "88px";
          taglineEl.style.left = "24px";
          break;
        case "top-right":
          taglineEl.style.top = "88px";
          taglineEl.style.right = "24px";
          break;
        case "top-left":
          taglineEl.style.top = "88px";
          taglineEl.style.left = "24px";
          break;
        case "bottom-right":
        default:
          taglineEl.style.bottom = "88px";
          taglineEl.style.right = "24px";
          break;
      }
    }
    taglineEl.style.opacity = "1";
  };

  let triggerCancelled = false;
  const cancelTrigger = () => { triggerCancelled = true; btn.remove(); taglineEl?.remove(); taglineEl = null; };
  whenBodyReady(() => { if (!triggerCancelled) document.body.appendChild(btn); });

  const context = dialog.context ?? defaultContext;
  const needsFloatingTabVerify = !!baseUrl;

  const finishWithFallback = () => {
    if (remoteLayout) {
      resolvedVariant = "floating";
      resolvedPosition = "bottom-right";
      applyTriggerLayout(resolvedVariant, resolvedPosition, defaultTabLabel);
    }
    showTrigger(baseBackground, defaultTabLabel);
  };

  const applyVerifyPayload = (payload: Record<string, unknown>) => {
    if (payload.allowed === false) {
      cancelTrigger();
      return;
    }
    const verifyVariant = getVerifyTriggerType(payload);
    const verifyPosition = getVerifyTriggerPosition(payload);
    if (remoteLayout) {
      resolvedVariant = verifyVariant ?? "floating";
      resolvedPosition = verifyPosition ?? "bottom-right";
    } else {
      if (verifyVariant) resolvedVariant = verifyVariant;
      if (verifyPosition) resolvedPosition = verifyPosition;
    }
    const tabLabel =
      typeof payload.widget_message_text === "string" &&
      payload.widget_message_text.trim()
        ? payload.widget_message_text
        : defaultTabLabel;
    applyTriggerLayout(resolvedVariant, resolvedPosition, tabLabel);
    const color =
      payload.widget_color ??
      payload.background ??
      payload.backgroundColor;
    if (typeof color === "string" && color.trim()) {
      showTrigger(color, tabLabel);
    } else {
      showTrigger(baseBackground, tabLabel);
    }

    const msgText = payload.widget_message_text;
    if (typeof msgText === "string" && msgText.trim()) {
      createTaglineBubble(msgText);
      positionTaglineBubble();
    }
  };

  if (needsFloatingTabVerify && baseUrl) {
    try {
      const pKey = verifyPrefetchKey(projectId, baseUrl, context);
      const verifyPromise =
        verifyPrefetch?.key === pKey ? verifyPrefetch.promise : fetchVerifyPayload(projectId, context, baseUrl);

      verifyPromise
        .then((data) => {
          if (triggerCancelled) return;
          if (!data || typeof data !== "object") {
            if (remoteLayout) finishWithFallback();
            else cancelTrigger();
            return;
          }
          applyVerifyPayload(data);
        })
        .catch(() => {
          if (remoteLayout) finishWithFallback();
          else cancelTrigger();
        });
    } catch {
      if (remoteLayout) finishWithFallback();
      else cancelTrigger();
    }
  } else {
    finishWithFallback();
  }
}

function doEmbed(targetEl: HTMLElement, options: EmbedOptions & { projectId: string }): void {
  const mergedContext =
    options.context && defaultContext
      ? { ...defaultContext, ...options.context }
      : options.context ?? defaultContext;

  const portalOptions: UserSoundPortalOptions = {
    projectId: options.projectId,
    baseUrl: options.baseUrl ?? defaultBaseUrl,
    context: mergedContext,
  };

  if (options.autoResize) {
    portalOptions.onResize = (_width: number, height: number) => {
      targetEl.style.height = `${height}px`;
    };
  }

  const portal = createUserSoundPortal(portalOptions);
  portal.mount(targetEl);
  portal.open();
}

function realEmbed(options: EmbedOptions): void {
  if (typeof document === "undefined") return;

  const projectId = effectiveProjectId(options.projectId);
  if (!projectId) {
    console.error("UserSound.embed: projectId is required");
    return;
  }

  const resolved = { ...options, projectId };

  if (typeof options.element === "string") {
    const selector = options.element;
    whenBodyReady(() => {
      const el = document.querySelector(selector);
      if (!(el instanceof HTMLElement)) {
        console.error(`UserSound.embed: no element matched selector ${JSON.stringify(selector)}`);
        return;
      }
      doEmbed(el, resolved);
    });
    return;
  }

  doEmbed(options.element, resolved);
}

function installGlobal(): UserSoundGlobal | undefined {
  if (typeof window === "undefined") return undefined;
  const impl: UserSoundGlobal = {
    init(c) {
      if (c.baseUrl) defaultBaseUrl = c.baseUrl;
      if (c.context) defaultContext = { ...c.context };
      if (c.projectId != null && String(c.projectId).trim() !== "") {
        defaultProjectId = String(c.projectId).trim();
      }
      if (defaultProjectId && defaultBaseUrl) {
        scheduleVerifyPrefetch(defaultProjectId, defaultContext, defaultBaseUrl);
      }
    },
    createPortal: realCreatePortal,
    create,
    embed: realEmbed,
  };
  window.UserSound = impl;
  return impl;
}

if (typeof window !== "undefined") {
  if (!window.UserSound) {
    window.UserSound = {
      init() {},
      createPortal() { throw new Error("UserSound.createPortal not available before SDK loaded."); },
      create() { throw new Error("UserSound.create not available before SDK loaded."); },
      embed() { throw new Error("UserSound.embed not available before SDK loaded."); },
    };
  }
  installGlobal();
  document.addEventListener("keydown", (e) => {
    if (!isOpen) return;
    if (e.key === "Escape" || e.key === "Esc") { e.preventDefault(); e.stopPropagation(); closeDialog(); }
  });
}
